import { ChangeDetectionStrategy, Component, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { CalculatorTTMData } from '../../../shared/models/calculator-ttm-data';
import { BaseCalculatorComponent } from '../base-calculator.component';

interface BigUpYieldResults {
  buyPrice: number;
  stickerPrice: number;
  targetYield: number;
  analysis: string;
}

@Component({
    standalone: true,
    selector: 'app-big-up-yield-calculator',
    imports: [
        CommonModule,
        ReactiveFormsModule
    ],
    templateUrl: './big-up-yield-calculator.component.html',
    styleUrls: ['./big-up-yield-calculator.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BigUpYieldCalculatorComponent extends BaseCalculatorComponent implements OnInit {
  bigUpYieldForm!: FormGroup;
  isCalculated = false;
  isLoading = signal(false);
  calculatedOwnerEarnings = 0;
  calculatedOwnerEarningsPerShare = 0;
  results: BigUpYieldResults = {
    buyPrice: 0,
    stickerPrice: 0,
    targetYield: 10,
    analysis: ''
  };

  constructor(private fb: FormBuilder) {
    super();
  }

  ngOnInit(): void {
    this.initializeForm();
    this.setupFormSubscriptions();
  }

  private initializeForm(): void {
    this.bigUpYieldForm = this.fb.group({
      cashFromOperations: [null, [Validators.required, Validators.min(0)]],
      capitalExpenditures: [null, [Validators.required, Validators.min(0)]],
      maintenanceCapExPercent: [70, [Validators.required, Validators.min(0), Validators.max(100)]],
      maintenanceCapEx: [null, [Validators.required, Validators.min(0)]],
      taxProvision: [null, [Validators.required, Validators.min(0)]],
      sharesOutstanding: [null, [Validators.required, Validators.min(0.01)]]
    });
  }

  private setupFormSubscriptions(): void {
    // Subscribe to form changes to update calculated fields
    this.bigUpYieldForm.valueChanges.subscribe(() => {
      this.updateCalculatedFields();
    });
  }

  private updateCalculatedFields(): void {
    const cashFromOperations = this.bigUpYieldForm.get('cashFromOperations')?.value || 0;
    const maintenanceCapEx = this.bigUpYieldForm.get('maintenanceCapEx')?.value || 0;
    const taxProvision = this.bigUpYieldForm.get('taxProvision')?.value || 0;
    const sharesOutstanding = this.bigUpYieldForm.get('sharesOutstanding')?.value || 0;

    // Calculate Owner's Earnings
    this.calculatedOwnerEarnings = cashFromOperations - maintenanceCapEx - taxProvision;

    // Calculate Owner's Earnings Per Share
    if (sharesOutstanding > 0) {
      this.calculatedOwnerEarningsPerShare = this.calculatedOwnerEarnings / sharesOutstanding;
    } else {
      this.calculatedOwnerEarningsPerShare = 0;
    }
  }

  updateMaintenanceCapEx(): void {
    const capitalExpenditures = this.bigUpYieldForm.get('capitalExpenditures')?.value || 0;
    const maintenanceCapExPercent = this.bigUpYieldForm.get('maintenanceCapExPercent')?.value || 70;

    // Calculate maintenance CapEx as percentage of total CapEx
    const calculatedMaintenanceCapEx = capitalExpenditures * (maintenanceCapExPercent / 100);

    // Update the maintenance CapEx field
    this.bigUpYieldForm.patchValue({
      maintenanceCapEx: Math.round(calculatedMaintenanceCapEx * 100) / 100
    });
  }

  /**
   * Populate form fields with TTM data from the calculator data service
   */
  protected override populateFormWithTtmData(ttmData: CalculatorTTMData): void {
    const updates: Partial<{
      cashFromOperations: number;
      capitalExpenditures: number;
      taxProvision: number;
      sharesOutstanding: number
    }> = {};

    // Pre-populate cash from operations (operatingCashFlow from backend)
    if (ttmData.operatingCashFlow !== null && ttmData.operatingCashFlow !== undefined) {
      updates.cashFromOperations = ttmData.operatingCashFlow;
    }

    // Pre-populate capital expenditures (capitalExpenditure from backend, note it's negative)
    if (ttmData.capitalExpenditure !== null && ttmData.capitalExpenditure !== undefined) {
      // Capital expenditure is typically negative in financial statements, so we use absolute value
      updates.capitalExpenditures = Math.abs(ttmData.capitalExpenditure);
    }

    // Pre-populate tax provision (incomeTaxExpense from backend)
    if (ttmData.incomeTaxExpense !== null && ttmData.incomeTaxExpense !== undefined) {
      updates.taxProvision = ttmData.incomeTaxExpense;
    }

    // Pre-populate shares outstanding (weightedAverageShsOutDil from backend, convert to millions)
    if (ttmData.weightedAverageShsOutDil !== null && ttmData.weightedAverageShsOutDil !== undefined) {
      // Backend provides shares in absolute numbers, need to convert to millions
      updates.sharesOutstanding = ttmData.weightedAverageShsOutDil;
    }

    // Update the form with the new values
    if (Object.keys(updates).length > 0) {
      this.bigUpYieldForm.patchValue(updates);
      // Update maintenance CapEx after setting capital expenditures
      if (updates.capitalExpenditures) {
        this.updateMaintenanceCapEx();
      }
      this.logger.debug('BigupYield Calculator pre-populated with TTM data:', updates);
    }
  }

  calculateBigUpYield(): void {
    if (this.bigUpYieldForm.invalid) return;

    this.isLoading.set(true);

    // Get form values
    const cashFromOperations = this.bigUpYieldForm.get('cashFromOperations')?.value || 0;
    const capitalExpenditures = this.bigUpYieldForm.get('capitalExpenditures')?.value || 0;
    const maintenanceCapExPercent = this.bigUpYieldForm.get('maintenanceCapExPercent')?.value || 70;
    const taxProvision = this.bigUpYieldForm.get('taxProvision')?.value || 0;
    const sharesOutstanding = this.bigUpYieldForm.get('sharesOutstanding')?.value || 0;
    const targetYield = 0.10; // 10% target yield

    // Simulate calculation delay
    setTimeout(() => {
      // Calculate using the exact formula provided
      const maintenanceCapEx = capitalExpenditures * (maintenanceCapExPercent / 100);
      const ownerEarnings = cashFromOperations - maintenanceCapEx - taxProvision;

      // Buy Price formula: ownerEarnings / (targetYield * sharesOutstanding)
      const buyPrice = ownerEarnings / (targetYield * sharesOutstanding);
      const stickerPrice = buyPrice * 2; // Double the buy price since formula already discounts

      const analysis = this.generateAnalysis(ownerEarnings, this.calculatedOwnerEarningsPerShare, targetYield * 100);

      this.results = {
        buyPrice: Math.round(buyPrice * 100) / 100,
        stickerPrice: Math.round(stickerPrice * 100) / 100,
        targetYield: targetYield * 100,
        analysis
      };

      this.isCalculated = true;
      this.isLoading.set(false);
    }, 1000);
  }

  private generateAnalysis(ownerEarnings: number, ownerEarningsPerShare: number, targetYield: number): string {
    if (ownerEarnings <= 0) {
      return "Negative owner's earnings indicate the company is not generating sufficient cash flow after maintenance capital expenditures and taxes. This investment may not be suitable for yield-focused strategies.";
    } else if (ownerEarningsPerShare <= 0) {
      return "Owner's earnings per share is negative or zero, suggesting poor cash generation relative to share count. Consider companies with stronger cash flow fundamentals.";
    } else if (ownerEarningsPerShare < 2) {
      return `Low owner's earnings per share ($${ownerEarningsPerShare.toFixed(2)}) suggests modest cash generation. The ${targetYield}% target yield requires strong cash flows - consider higher-yielding alternatives or wait for better entry prices.`;
    } else if (ownerEarningsPerShare < 5) {
      return `Moderate owner's earnings per share ($${ownerEarningsPerShare.toFixed(2)}) provides decent cash generation. The ${targetYield}% target yield is achievable, making this a reasonable value play for income-focused investors.`;
    } else if (ownerEarningsPerShare < 10) {
      return `Strong owner's earnings per share ($${ownerEarningsPerShare.toFixed(2)}) indicates excellent cash generation after maintenance CapEx. The ${targetYield}% target yield provides attractive returns which beats the risk free treasury rate.`;
    } else {
      return `Exceptional owner's earnings per share ($${ownerEarningsPerShare.toFixed(2)}) demonstrates outstanding cash flow generation. The ${targetYield}% target yield offers excellent value with substantial upside potential - a true "Big Up" opportunity.`;
    }
  }

  clearForm(): void {
    this.bigUpYieldForm.reset();
    this.bigUpYieldForm.patchValue({
      maintenanceCapExPercent: 70 // Reset to default
    });
    this.isCalculated = false;
    this.calculatedOwnerEarnings = 0;
    this.calculatedOwnerEarningsPerShare = 0;
    this.results = {
      buyPrice: 0,
      stickerPrice: 0,
      targetYield: 10,
      analysis: ''
    };
  }
}

